/**
 * TUT-245 — o vocabulário FECHADO da Agenda: a visão e o tipo de compromisso.
 *
 * Mora em `lib/` pelo mesmo motivo do `transport-modes.ts`: duas superfícies
 * distintas dependem dele — a página e o `validateSearch` da rota. Deixá-lo em
 * `pages/AgendaPage.tsx` obrigaria o roteador a importar a tela inteira só
 * para conferir uma string, e a `AgendaPage` é carregada sob demanda (`lazy`)
 * justamente para não entrar no pedaço inicial.
 *
 * Aqui não há regra nenhuma: o que este arquivo guarda é a LISTA e os rótulos.
 *
 * **`role` saiu nesta fatia.** A Agenda tinha um filtro "papel" com
 * `Responsável interno` e `Perito` ao lado do filtro de tipo — e ele era
 * duplicata disfarçada: prazo é SEMPRE interno e perícia é SEMPRE perito
 * (`montarEventosDaAgenda` os grava assim, não os deriva). Dois controles para
 * o mesmo recorte só produzem a combinação vazia — "Prazos" + "Perito" devolve
 * uma agenda em branco sem explicar por quê. O papel continua no CARTÃO, onde
 * é informação; deixou de ser filtro, onde era armadilha.
 */

export const AGENDA_VIEWS = [
  "month",
  "week",
  "day",
  "quarter",
  "range",
] as const;

export type AgendaView = (typeof AGENDA_VIEWS)[number];

export const agendaViewLabels: Record<AgendaView, string> = {
  month: "Mês",
  week: "Semana",
  day: "Dia",
  quarter: "Trimestre",
  range: "Período",
};

/**
 * O tipo do compromisso. Ausente na URL = todos.
 *
 * TUT-279 fase 2 — `"meeting"` entra como o SEGUNDO tipo de compromisso, ao
 * lado da perícia. O valor da casa é `meeting`, não `reuniao`
 * (`list-filters-na-url.test.ts` continua recusando `reuniao`).
 */
export const AGENDA_KINDS = ["deadline", "expertise", "meeting"] as const;

export type AgendaKind = (typeof AGENDA_KINDS)[number];

export const agendaKindLabels: Record<AgendaKind, string> = {
  deadline: "Prazos",
  expertise: "Perícias",
  meeting: "Reuniões",
};

/**
 * A visão pedida, ou `undefined` — a mesma função na ROTA e na TELA.
 *
 * Duas conferências, e a segunda não é redundante: o `useSearch()` da filha
 * chega mesclado com a busca do pai, que não valida nada. Foi assim que
 * `?mode=FOGUETE` deixou a tela de Viagens sem nenhum chip aceso, em 31/08
 * (TUT-204g) — a rota descartava, e o valor reaparecia na leitura. Aqui as
 * duas camadas perguntam à MESMA lista, então não há como uma envelhecer.
 */
export function visaoDaAgenda(valor: unknown): AgendaView | undefined {
  return typeof valor === "string" &&
    (AGENDA_VIEWS as readonly string[]).includes(valor)
    ? (valor as AgendaView)
    : undefined;
}

/**
 * Os tipos pedidos — vazio é "o padrão" (perícia e reunião, e prazo só com
 * "Mostrar prazos"). Ver `visaoDaAgenda`.
 *
 * TUT-342 — escolha MÚLTIPLA (pedido do dono em 28/09: "selecionar mais de um
 * filtro, tipo prazo e perícia"). A URL repete a chave (`tipo=deadline&tipo=
 * expertise`), e o valor único de antes continua valendo, porque uma string é
 * lida como a lista de um item. O que não é tipo conhecido cai fora aqui e na
 * rota, pela mesma lista.
 */
export function tiposDaAgenda(valor: unknown): AgendaKind[] {
  const pedidos = Array.isArray(valor) ? valor : [valor];
  return AGENDA_KINDS.filter((tipo) => pedidos.includes(tipo));
}

/**
 * TUT-342 — os filtros de identificador OPACO da Agenda (assunto, cliente,
 * responsável), como lista: a string única de antes é a lista de um item, e o
 * que não é texto cai fora. Quem descarta o identificador que não existe é a
 * tela, que conhece os catálogos (TUT-245).
 */
export function idsDaBusca(valor: unknown): string[] {
  const pedidos = Array.isArray(valor) ? valor : [valor];
  return [
    ...new Set(
      pedidos.filter((item): item is string => typeof item === "string" && item !== ""),
    ),
  ];
}
